import { useState } from 'react'
import { FileText, Download, Trash2, Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { toast } from 'sonner'
import { documentsAPI } from '@/utils/api'
import type { Document } from './types'

interface DocumentsListCardProps {
  documents: Document[]
  onUpdate: () => void
}

export function DocumentsListCard({ documents, onUpdate }: DocumentsListCardProps) {
  const [downloadingDocId, setDownloadingDocId] = useState<number | null>(null)
  const [deletingDocId, setDeletingDocId] = useState<number | null>(null)

  const handleDownload = async (docId: number) => {
    if (downloadingDocId) return
    setDownloadingDocId(docId)
    try {
      const response = await documentsAPI.getDownloadUrl(docId)
      if (response.status === 'success' && response.download_url) {
        window.open(response.download_url, '_blank')
      } else {
        toast.error('Download-URL konnte nicht abgerufen werden')
      }
    } catch (error: unknown) {
      console.error('Download failed:', error)
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 403) {
        toast.error('Sie haben keine Berechtigung, dieses Dokument herunterzuladen')
      } else if (axiosError.response?.status === 404) {
        toast.error('Dokument nicht gefunden')
      } else {
        toast.error('Dokument konnte nicht heruntergeladen werden')
      }
    } finally {
      setDownloadingDocId(null)
    }
  }

  const handleDelete = async (docId: number) => {
    setDeletingDocId(docId)
    try {
      await documentsAPI.deleteDocument(docId)
      toast.success('Dokument gelöscht')
      onUpdate()
    } catch (err) {
      console.error('Failed to delete document:', err)
      toast.error('Fehler beim Löschen')
    } finally {
      setDeletingDocId(null)
    }
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <FileText className="h-5 w-5 text-slate-400" />
        Dokumente
        <Badge variant="secondary" className="ml-auto">
          {documents.length}
        </Badge>
      </h2>
      {documents.length === 0 ? (
        <p className="text-slate-500 text-sm">Noch keine Dokumente hochgeladen.</p>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors group"
            >
              <FileText className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-700 truncate flex-1">
                {doc.kind_display || doc.kind}
              </span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleDownload(doc.id)}
                  disabled={downloadingDocId === doc.id}
                  className="p-1 rounded hover:bg-slate-200 transition-colors disabled:opacity-50"
                  title="Herunterladen"
                >
                  {downloadingDocId === doc.id ? (
                    <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 text-slate-500" />
                  )}
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      className="p-1 rounded hover:bg-red-100 transition-colors"
                      title="Löschen"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Dokument löschen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Dieses Dokument wird unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(doc.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Löschen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
